import React from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, TouchableOpacity } from 'react-native';

import Avatar from '@components/Avatar/Avatar';
import Highlighter from '@components/HighlightWords';
import { ContactType } from '@components/Invite/Contact';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import Icon from '@components/shared/Icon/Icon';
import { RestyleProps } from '@components/shared/Icon/RestyleIcon';
import { UserSearchType } from '@components/TaskList/TaskInviteOrAssignee.web';
import { TaskMemberRole } from '@graphql/generated';
import theme from '@themes/theme';

type ContactGroupMemberProps = {
  style?: RestyleProps;
  isVisible: boolean;
  contact: ContactType | null;
  filterVal?: string;
  isEditing?: boolean;
  onPress: (item: ContactType) => void;
  openModal?: (item: ContactType) => void;
  currentUserRole?: TaskMemberRole;
  selectedUserList?: UserSearchType[];
  disabledUsersId?: string[];
};

const ContactGroupMemberNew: React.FC<ContactGroupMemberProps> = ({
  style,
  isVisible,
  contact,
  filterVal,
  isEditing,
  onPress,
  openModal,
  currentUserRole,
  selectedUserList,
  disabledUsersId,
}) => {
  const { t } = useTranslation('models');

  const getDisplayRole = (role: string) => {
    return role === TaskMemberRole.Viewer ? 'Viewer' : 'Assignee';
  };
  return (
    <Box {...style}>
      {isVisible &&
        contact?.users?.map((user) => {
          const selectedUser = selectedUserList?.find((u) => u.id === user.id);
          const roleLabel = selectedUser
            ? getDisplayRole(selectedUser.role)
            : user?.roleLabel;
          const disabledUser = disabledUsersId?.some((u) => u === user.id);
          return (
            <Box
              marginTop='xxs'
              flexDirection='row'
              alignItems='center'
              paddingVertical='xs'
              key={user?.id}>
              <Avatar
                id={user?.id}
                label={user?.name}
                avatar={user?.avatar}
                size='medium'
                disableAvatarPress={false}
              />
              <Box marginLeft='xs' flex={1}>
                <Text
                  accessibilityLabel='Member'
                  variant='body'
                  numberOfLines={1}
                  color={
                    user?.displayName ?? user?.name ? 'textPrimary' : 'grey04'
                  }>
                  {user?.displayName ?? user?.name ? (
                    <Highlighter
                      highlightStyle={
                        Platform.OS == 'web'
                          ? {
                              backgroundColor: theme.colors.yellowBright,
                            }
                          : {
                              color: theme.colors.greenSecondary,
                              backgroundColor: theme.colors.background,
                            }
                      }
                      autoEscape
                      searchWords={filterVal?.split(' ') || ''}
                      textToHighlight={user?.displayName ?? user?.name}
                    />
                  ) : (
                    'No name'
                  )}
                </Text>
                {roleLabel && !disabledUser && (
                  <TouchableOpacity
                    disabled={currentUserRole !== TaskMemberRole.Owner}
                    onPress={() => {
                      openModal?.(selectedUser ?? user);
                    }}>
                    <Box marginTop='xxs' flexDirection='row'>
                      <Text variant='bodyTaskName' color='black'>
                        {t(roleLabel)}
                      </Text>
                      {currentUserRole === TaskMemberRole.Owner && (
                        <Icon
                          variant='s'
                          name='ChevronDown'
                          color='black'
                          marginLeft='xs'
                          onPress={() => {
                            openModal?.(selectedUser ?? user);
                          }}
                          accessibilityLabel='RoleDropDown'
                        />
                      )}
                    </Box>
                  </TouchableOpacity>
                )}
                {roleLabel && disabledUser && (
                  <Box
                    mt='xxs'
                    mr='m'
                    alignItems='center'
                    justifyContent='flex-start'
                    flexDirection='row'>
                    <Text variant='webBodyHintSubline' color='grey04'>
                      {roleLabel}
                    </Text>
                    <Box
                      mx='xxs'
                      width={4}
                      height={4}
                      borderRadius='xxxs'
                      backgroundColor='grey04'
                    />
                    <Text
                      variant='webBodyHintSubline'
                      color='greenSecondary'
                      flexShrink={1}
                      ellipsizeMode='tail'
                      numberOfLines={1}>
                      Already assignee
                    </Text>
                  </Box>
                )}
              </Box>
              {isEditing && (
                <Box justifyContent='center'>
                  <RadioButton
                    onPress={() => {
                      onPress?.(selectedUser ?? user);
                    }}
                    isSelected={
                      disabledUsersId?.includes(user.id) ||
                      !!selectedUserList?.some((u) => u.id === user.id)
                    }
                    iconVariant='m'
                    disabled={disabledUsersId?.includes(user.id)}
                    isTaskAssignee
                  />
                </Box>
              )}
            </Box>
          );
        })}
    </Box>
  );
};

export default ContactGroupMemberNew;
